/* =========================================================
   CubShine theme JavaScript
   Small and library-free. Everything important (menus, add to
   cart, cart updates, filters, forms) still works without it;
   this file just makes things smoother.
   ========================================================= */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  // Wait until someone stops typing/clicking before doing something
  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  // Only set an element up once, even if this runs again
  const firstTime = (el) => {
    if (el.dataset.ready === 'true') return false;
    el.dataset.ready = 'true';
    return true;
  };

  /* ---------- 1. Announcement bar ---------- */
  function initAnnouncements(root) {
    root.querySelectorAll('[data-announcement]').forEach((bar) => {
      if (!firstTime(bar)) return;
      const pauseButton = bar.querySelector('[data-announcement-pause]');
      let paused = false;

      const setPaused = (value) => {
        paused = value;
        bar.classList.toggle('is-paused', paused);
        if (pauseButton) {
          pauseButton.setAttribute('aria-pressed', String(paused));
          pauseButton.setAttribute('aria-label', paused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause);
        }
      };

      if (pauseButton) pauseButton.addEventListener('click', () => setPaused(!paused));

      if (bar.dataset.mode !== 'rotate') return;

      const slides = Array.from(bar.querySelectorAll('[data-announcement-slide]'));
      if (slides.length < 2) return;
      let index = 0;

      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.hidden = i !== index;
        });
      };

      const prev = bar.querySelector('[data-announcement-prev]');
      const next = bar.querySelector('[data-announcement-next]');
      if (prev) prev.addEventListener('click', () => show(index - 1));
      if (next) next.addEventListener('click', () => show(index + 1));

      // In the theme editor, show the message you click on
      bar.addEventListener('shopify:block:select', (event) => {
        const i = slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (i > -1) {
          show(i);
          setPaused(true);
        }
      });

      const seconds = Math.max(3, Number(bar.dataset.speed) || 5);
      const timer = setInterval(() => {
        if (!bar.isConnected) {
          clearInterval(timer);
          return;
        }
        const busy = bar.matches(':hover') || bar.contains(document.activeElement);
        if (!paused && !busy && !reduceMotion.matches) show(index + 1);
      }, seconds * 1000);
    });
  }

  /* ---------- 2. Header: menus, search pop-up, sticky shadow ---------- */
  function closeMenuDrawer(drawer, focusToggle) {
    if (!drawer || !drawer.open) return;
    drawer.open = false;
    if (focusToggle) drawer.querySelector('summary').focus();
  }

  function initHeader(root) {
    root.querySelectorAll('[data-menu-drawer]').forEach((drawer) => {
      if (!firstTime(drawer)) return;
      drawer.addEventListener('toggle', () => {
        document.body.classList.toggle('overflow-hidden', drawer.open);
      });
      drawer.querySelectorAll('[data-menu-close]').forEach((el) => {
        el.addEventListener('click', () => closeMenuDrawer(drawer, true));
      });
      drawer.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          if (!link.hasAttribute('data-search-open')) closeMenuDrawer(drawer, false);
        });
      });
    });

    root.querySelectorAll('[data-dropdown]').forEach((dropdown) => {
      if (!firstTime(dropdown)) return;
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        document.querySelectorAll('[data-dropdown][open]').forEach((other) => {
          if (other !== dropdown) other.open = false;
        });
      });
    });

    setHeaderHeight();
  }

  // Remember how tall the header is, so #links stop just below it
  function setHeaderHeight() {
    const header = document.querySelector('.section-header');
    if (header) document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  }

  // Close menus/dropdowns with the Escape key or by clicking elsewhere
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-dropdown][open], [data-facet][open]').forEach((details) => {
      details.open = false;
      details.querySelector('summary').focus();
    });
    closeMenuDrawer(document.querySelector('[data-menu-drawer][open]'), true);
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-dropdown][open], [data-facet][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  // Search pop-up
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-search-open]');
    if (opener) {
      const dialog = document.querySelector('[data-search-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return; // falls back to the search page
      event.preventDefault();
      closeMenuDrawer(document.querySelector('[data-menu-drawer][open]'), false);
      dialog.showModal();
      const input = dialog.querySelector('input[type="search"]');
      if (input) input.focus();
      return;
    }
    const closer = event.target.closest('[data-search-close]');
    if (closer) closer.closest('dialog').close();
    if (event.target.matches && event.target.matches('[data-search-dialog]')) event.target.close();
  });

  // Soft shadow under a sticky header once the page scrolls
  window.addEventListener(
    'scroll',
    () => {
      const header = document.querySelector('[data-header]');
      if (header) header.classList.toggle('is-scrolled', window.scrollY > 4);
    },
    { passive: true }
  );
  window.addEventListener('resize', debounce(setHeaderHeight, 150));

  /* ---------- 3. Product page: photos, options, price ---------- */
  function initGallery(gallery) {
    const track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;
    const items = Array.from(track.children);
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    let current = 0;

    const setActive = (index) => {
      current = index;
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
      const counter = gallery.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = `${index + 1} / ${items.length}`;
    };

    const goTo = (index) => {
      const item = items[Math.max(0, Math.min(items.length - 1, index))];
      if (!item) return;
      track.scrollTo({ left: item.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      setActive(items.indexOf(item));
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));

    track.addEventListener(
      'scroll',
      debounce(() => setActive(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))), 80),
      { passive: true }
    );

    gallery.addEventListener('gallery:show', (event) => {
      const index = items.findIndex((item) => item.dataset.mediaId === String(event.detail));
      if (index > -1) goTo(index);
    });

    // Start on the photo of the selected variant (e.g. when the link has ?variant=...)
    const startIndex = items.findIndex((item) => item.dataset.mediaId === gallery.dataset.initialMedia);
    if (startIndex > 0) {
      track.scrollLeft = items[startIndex].offsetLeft;
      setActive(startIndex);
    }
  }

  function updateVariant(section, variant) {
    const form = document.getElementById(`ProductForm-${section.dataset.sectionId}`);
    const button = section.querySelector('[data-add-button]');
    const availability = section.querySelector('[data-availability]');

    if (button) {
      let label = button.dataset.labelAdd;
      if (!variant) label = button.dataset.labelUnavailable;
      else if (!variant.available) label = button.dataset.labelSoldOut;
      button.disabled = !variant || !variant.available;
      const text = button.querySelector('[data-add-text]');
      if (text) text.textContent = label;
    }

    if (availability) {
      const labels = {
        in_stock: availability.dataset.labelInStock,
        available: availability.dataset.labelAvailable,
        sold_out: availability.dataset.labelSoldOut,
        unavailable: availability.dataset.labelUnavailable,
      };
      let state = 'unavailable';
      if (variant && variant.available) state = variant.in_stock ? 'in_stock' : 'available';
      else if (variant) state = 'sold_out';
      availability.dataset.state = state;
      availability.querySelector('[data-availability-text]').textContent = labels[state];
    }

    if (!variant) return;

    if (form) {
      const input = form.querySelector('[name="id"]');
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    section.querySelectorAll('[data-price]').forEach((el) => {
      el.innerHTML = variant.price_html;
    });

    if (!designMode) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    if (variant.media_id) {
      const gallery = section.querySelector('[data-gallery]');
      if (gallery) gallery.dispatchEvent(new CustomEvent('gallery:show', { detail: variant.media_id }));
    }
  }

  function initProducts(root) {
    root.querySelectorAll('[data-product]').forEach((section) => {
      if (!firstTime(section)) return;
      const gallery = section.querySelector('[data-gallery]');
      if (gallery) initGallery(gallery);

      const picker = section.querySelector('[data-variant-picker]');
      const json = section.querySelector('[data-variants-json]');
      if (!picker || !json) return;
      const variants = JSON.parse(json.textContent);

      picker.addEventListener('change', () => {
        const selected = Array.from(picker.querySelectorAll('[data-option]')).map((group) => {
          const input = group.querySelector('input:checked') || group.querySelector('select');
          const value = input ? input.value : null;
          const label = group.querySelector('[data-selected-value]');
          if (label && value) label.textContent = value;
          return value;
        });
        const variant = variants.find((v) => v.options.every((option, i) => option === selected[i]));
        updateVariant(section, variant);
      });
    });
  }

  /* ---------- 4. Quantity +/- buttons (product page and cart) ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-change]');
    if (!button) return;
    const input = button.closest('[data-qty]').querySelector('input');
    if (!input) return;
    const min = input.min === '' ? 0 : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(button.dataset.qtyChange)));
    if (next !== Number(input.value)) {
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ---------- 5. Cart: update automatically when a quantity changes ---------- */
  const submitCart = debounce((form) => {
    const updateButton = form.querySelector('[name="update"]');
    form.classList.add('is-updating');
    if (form.requestSubmit) form.requestSubmit(updateButton);
    else updateButton.click();
  }, 700);

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-cart-qty]')) submitCart(event.target.form);
  });

  /* ---------- 6. Shop filters and sorting: apply as soon as something changes ---------- */
  const sendFacets = (form) => {
    // Empty price boxes would add messy blanks to the address, so skip them
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.disabled = input.value === '';
    });
    form.submit();
  };
  const sendFacetsSlowly = debounce(sendFacets, 900);

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    if (event.target.type === 'number') sendFacetsSlowly(form);
    else sendFacets(form);
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-facets-form]')) return;
    event.target.querySelectorAll('input[type="number"]').forEach((input) => {
      input.disabled = input.value === '';
    });
  });

  /* ---------- 7. FAQ search box ---------- */
  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-faq-search]')) return;
    const section = event.target.closest('[data-faq]');
    const query = event.target.value.trim().toLowerCase();
    let shown = 0;
    section.querySelectorAll('[data-faq-item]').forEach((item) => {
      const match = !query || item.textContent.toLowerCase().includes(query);
      item.hidden = !match;
      if (match) shown += 1;
    });
    const empty = section.querySelector('[data-faq-empty]');
    if (empty) empty.hidden = shown > 0;
  });

  /* ---------- 8. Country / language selectors in the footer ---------- */
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-localization-select]')) event.target.form.submit();
  });

  /* ---------- 9. "You might also like" product recommendations ---------- */
  function initRecommendations(root) {
    root.querySelectorAll('[data-recommendations]').forEach((el) => {
      if (!firstTime(el) || !el.dataset.url) return;
      fetch(el.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim().length) {
            el.innerHTML = fresh.innerHTML;
            el.classList.add('is-loaded');
          }
        })
        .catch(() => {
          /* If it fails, the section simply stays hidden */
        });
    });
  }

  /* ---------- Start everything ---------- */
  function init(root) {
    initAnnouncements(root);
    initHeader(root);
    initProducts(root);
    initRecommendations(root);
  }

  init(document);

  // When you change a section in the theme editor, set that section up again
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
